// CRA's dev-server error overlay escalates the benign "ResizeObserver loop"
// browser notice into a fatal-looking overlay. The notice is harmless — the
// observer simply re-delivers measurements on the next animation frame (no data
// loss, no infinite loop) — and it never appears in a production build. React
// Flow's canvas trips it as nodes stream in (e.g. while a voice turn builds the
// flow). We swallow ONLY these two exact messages so they don't obscure the UI
// in development; every other error propagates to the overlay as normal.

export const isBenignResizeObserverError = (message: string | undefined | null): boolean =>
  !!message &&
  /^ResizeObserver loop (completed with undelivered notifications|limit exceeded)/.test(message);

export function installResizeObserverErrorSuppressor(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener(
    'error',
    (e) => {
      if (isBenignResizeObserverError(e.message)) {
        // Capture phase + stopImmediatePropagation. NOTE: this alone does NOT
        // beat CRA's dev overlay — the overlay registers its window 'error'
        // listener before app code, and for window-targeted events listeners
        // fire in registration order at the target, so the overlay sees it
        // first. The real fix is installResizeObserverLoopFix() below, which
        // stops the error being thrown at all. This stays as cheap backup.
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    },
    true,
  );
}

// Root-cause fix: wrap ResizeObserver so each delivery runs on the next
// animation frame instead of synchronously inside the browser's observation
// cycle. The benign "ResizeObserver loop completed with undelivered
// notifications" error fires when a RO callback mutates layout such that
// another delivery is needed in the SAME frame; deferring to rAF breaks that
// re-entrancy, so the notice is never generated (and the dev overlay never
// shows it). React Flow tolerates the one-frame delay. No-op where
// ResizeObserver is absent (e.g. jsdom in tests).
export function installResizeObserverLoopFix(): void {
  if (typeof window === 'undefined' || typeof window.ResizeObserver === 'undefined') return;
  const Native = window.ResizeObserver;
  // Avoid double-patching across HMR reloads.
  if ((Native as unknown as { __loopFixed?: boolean }).__loopFixed) return;

  class PatchedResizeObserver extends Native {
    constructor(callback: ResizeObserverCallback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          // If the observer was disconnected before the frame ran, the
          // callback is a harmless no-op.
          callback(entries, observer);
        });
      });
    }
  }
  (PatchedResizeObserver as unknown as { __loopFixed?: boolean }).__loopFixed = true;
  window.ResizeObserver = PatchedResizeObserver;
}
